import React, { useEffect, useRef, useState } from "react";
import { Box, Grid, Tooltip } from "@mui/material";
import { Button, Card } from "react-bootstrap";
import DeleteSweepIcon from "@mui/icons-material/DeleteSweep";
import DriveFileRenameOutlineIcon from "@mui/icons-material/DriveFileRenameOutline";
import { GridColDef } from "@mui/x-data-grid";
import { useMutation } from "react-query";
// import errorFishImageJpg from '../../_metronic/assets/errorFishImage.jpg'
import { deleteSpecies, getAllSpeciesList } from "../../api";
import useDebounce from "../../hooks/useDebounce";
import { APP_URLs } from "../../constants/appURLs";
import { charValidate } from "../../components/utility";
import CustomTooltip from "../../_metronic/layout/components/common/CustomTooltip";
import { KTCard, KTIcon } from "../../_metronic/helpers";
import MuiTable from "../../components/table/MuiTable";
import AddEditSpecies from "./AddEditSpecies";
import { resetStep, setSpeciesFormData, resetFormData } from "../../store/slice/form/speciesForm";
import { useDispatch } from "react-redux";
import ConfirmEventPopup from "../../_metronic/layout/components/common/ConfirmEventPopup";
import { snackActions } from "../../helpers/SnackUtilsConfigurator";

const Species: React.FC = () => {
  const [tableOptions, setTableOptions] = useState({
    offset: 0,
    limit: 10,
    hasNextPage: true,
    hasPrevPage: true,
    totalRowCount: undefined,
    search: "",
  });

  const tableRef = useRef();
  const dispatch = useDispatch();
  const debounceSearchValue: string = useDebounce(tableOptions?.search, 500);

  const [showAddEditModal, setShowAddEditModal] = useState(false);
  const [selectedRow, setSelectedRow] = useState<any>();
  const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false);

  const {
    mutateAsync: getSpeciesListAPI,
    data: speciesData,
    isLoading: isLoading,
  } = useMutation("species-list", getAllSpeciesList);

  const { mutateAsync: deleteSpeciesAPI, isLoading: isDeleting } =
  useMutation("delete-baits", deleteSpecies);

  useEffect(() => {
    getAllSpecies(tableOptions);
  }, []);

  useEffect(() => {
    getAllSpecies({
      ...tableOptions,
      search: debounceSearchValue ? debounceSearchValue : "",
    });
  }, [debounceSearchValue]);

  const getAllSpecies = async (
    query:
      | {
          offset: number | string;
          limit: number;
          search: string;
        }
      | undefined
  ) => {
    let response = await getSpeciesListAPI(query);
    if (response?.status) {
      setTableOptions((prevState: any) => {
        return {
          ...prevState,
          offset: response?.data?.offset,
          limit: response?.data?.limit,
          hasNextPage: response?.data?.hasNextPage,
          hasPrevPage: response?.data?.hasPrevPage,
          totalRowCount: response?.data?.totalDocs,
        };
      });
    }
  };

  const handleImageError = (event: any) => {
    // event.target.src = errorFishImageJpg;
  };

  const columns: GridColDef[] = [
    {
      field: "image",
      headerName: "Image",
      flex: 0.3,
      sortable: false,
      renderCell: (data: any) =>
        (
          <img
            style={{ height: "3rem", width: "3rem" }}
            src={`${APP_URLs.awsS3ImageURL}${data?.row?.image?.[0]}`} // Access only the first image
            onError={handleImageError}
          />
        ) ?? "N/A",
    },    
    {
      field: "name",
      headerName: "Name",
      flex: 0.4,
      sortable: false,
      renderCell: (data) => (
        <CustomTooltip
          title={
            data?.row?.name ? (
              <h6 style={{ color: "white" }}>{data?.row?.name}</h6>
            ) : (
              "N/A"
            )
          }
        >
          <span>
            {data?.row?.name ? charValidate(data?.row?.name, 30) : "N/A"}
          </span>
        </CustomTooltip>
      ),
    },
    {
      field: "description",
      headerName: "Description",
      flex: 0.4,
      sortable: false,
      renderCell: (data) => (
        <CustomTooltip
          title={
            data?.row ? (
              <h6 style={{ color: "white" }}>{data?.row?.description}</h6>
            ) : (
              "N/A"
            )
          }
        >
          <span
        dangerouslySetInnerHTML={{
          __html: charValidate(data?.row?.description, 30) ?? "N/A"
        }}
      />
        </CustomTooltip>
      ),
    },
    {
      field: "actions",
      headerName: "Actions",
      flex: 0.2,
      sortable: false,
      headerAlign: "center",
      align: "right",
      renderCell: (data) => (
        <Box display="flex" alignItems="center">
          <Tooltip title="Edit">
            <DriveFileRenameOutlineIcon
              onClick={() => {
                setSelectedRow(data.row);
                setShowAddEditModal(true);
                dispatch(setSpeciesFormData(data.row))
              }}
              sx={{ fontSize: 22, cursor: "pointer", color: "#07427c" }}
            />
          </Tooltip>
          {/* {isSuperAdmin && ( */}
          <Tooltip title="Delete">
            <DeleteSweepIcon
              onClick={() => {
                setSelectedRow(data.row);
                setShowDeleteConfirmModal(true);
              }}
              sx={{ fontSize: 22, ml: 1.7, cursor: "pointer" }}
              color="error"
            />
          </Tooltip>
          {/* )} */}
        </Box>
      ),
    },
  ];

  const handlePagination = (values: { page: number; pageSize: number }) => {
    let page = values?.page;
    let pageSize = values?.pageSize;
    let query = {
      offset:
        pageSize == tableOptions?.limit ? (page === 0 ? 0 : `${page}` + 0) : 0,
      limit: pageSize ?? 10,
      search: debounceSearchValue ?? "",
    };
    getAllSpecies(query);
  };

  const handleChangeFilters = (fieldName: string, value: string) => {
    setTableOptions((prevState: any) => {
      return {
        ...prevState,
        [fieldName]: value,
      };
    });
  };

  const handleModalClose = (isSubmit: boolean) => {
    dispatch(resetStep());
    setShowAddEditModal(false);
    setSelectedRow("");
    if (isSubmit) {
      getAllSpecies(tableOptions);
    }
  };

  const deleteSpeciesFunc = async () => {
    let response = await deleteSpeciesAPI({ id: selectedRow?._id });
    if (response?.status) {
      snackActions.success(response?.message);
      handleCloseDelete();
      getAllSpecies(tableOptions);
    } else {
      snackActions.error(response?.message);
    }
  };

  const handleCloseDelete = () => {
    setShowDeleteConfirmModal(false);
    setSelectedRow("");
  };

  return (
    <Card className="p-6">
      <>
        <KTCard>
          <MuiTable
            columns={columns}
            isAddEnable={false}
            data={speciesData?.data?.docs ?? []}
            tableRef={tableRef}
            loading={isLoading}
            isBackEnable={false}
            isRowClickable={true}
            handlePagination={handlePagination}
            tableOptions={tableOptions}
            filters={
              <Grid container spacing={2} pb={3}>
                <Grid item lg={3} md={4} xs={12}>
                  <Box>
                    <label
                      className="form-label fw-bold"
                      style={{ marginLeft: 5 }}
                    >
                      Search By Name
                    </label>
                    <input
                      type="text"
                      className="form-control form-control-lg form-control-solid"
                      placeholder="Search by name"
                      aria-label="Search"
                      onChange={(e: any) =>
                        handleChangeFilters("search", e?.target?.value)
                      }
                      value={tableOptions?.search}
                    />
                  </Box>
                </Grid>
                {/* {isSuperAdmin && ( */}
                <div className="ms-auto" style={{ marginTop: 43 }}>
                  <Button
                    className="d-flex align-items-center"
                    onClick={() => {setShowAddEditModal(true); dispatch(resetStep()); dispatch(resetFormData())}}
                  >
                    <KTIcon iconName="plus" className="fs-1" />
                    Add Species
                  </Button>
                </div>
                {/* )} */}
              </Grid>
            }
          />
        </KTCard>
        {showAddEditModal && (
          <AddEditSpecies
            editData={selectedRow}
            show={showAddEditModal}
            handleClose={handleModalClose}
          />
        )}
          {showDeleteConfirmModal && (
          <ConfirmEventPopup
            showPopup={showDeleteConfirmModal}
            handleClose={handleCloseDelete}
            handleConfirmed={deleteSpeciesFunc}
            loading={isDeleting}
            title={"Delete Bait"}
            message={"Are you sure, want to delete this bait?"}
          />
        )}
      </>
    </Card>
  );
};

export default Species;
